import React, { useEffect, useMemo, useState } from "react";
import Header from "../layouts/Header";
import Footer from "../layouts/Footer";
import { Badge, Card, Col, Row, Table, Alert, Spinner, ButtonGroup, ToggleButton } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../services/axiosInterceptor";
import { getToken } from "../services/authorize";
import ReactApexChart from "react-apexcharts";
import { libraryMenu } from "../data/Menu";

const QUICK_NAV_LINKS = ["/library/circulation", "/library/renewals", "/library/reservations", "/library/fines", "/library/book-search"];
const quickNavItems = libraryMenu.filter(m => QUICK_NAV_LINKS.includes(m.link));

const TIME_RANGE_OPTIONS = [
  { value: "7d", label: "7 วัน" },
  { value: "30d", label: "30 วัน" },
  { value: "3m", label: "3 เดือน" }
];

const FINE_REASON_LABELS = {
  overdue: "เกินกำหนด",
  damage: "ชำรุด",
  loss: "สูญหาย",
  other: "อื่นๆ"
};

const RESERVATION_STATUS_LABELS = {
  queued: "รอคิว",
  ready: "พร้อมรับ",
  fulfilled: "รับแล้ว",
  cancelled: "ยกเลิก",
  expired: "หมดอายุ"
};

export default function LibraryHome() {
  const navigate = useNavigate();
  const [resourceSummary, setResourceSummary] = useState({
    total_personnel: 0,
    total_active_students: 0
  });
  const [masterData, setMasterData] = useState({
    categories: [],
    locations: [],
    teachers: []
  });
  const [titles, setTitles] = useState([]);
  const [copies, setCopies] = useState([]);
  const [loans, setLoans] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [fines, setFines] = useState([]);
  const [timeRange, setTimeRange] = useState("30d");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  const formatNumber = (value) => Number(value || 0).toLocaleString("th-TH");
  const formatCurrency = (value) =>
    Number(value || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const isSameDay = (dateA, dateB) =>
    dateA.getFullYear() === dateB.getFullYear() &&
    dateA.getMonth() === dateB.getMonth() &&
    dateA.getDate() === dateB.getDate();

  const isSameMonth = (dateA, dateB) =>
    dateA.getFullYear() === dateB.getFullYear() && dateA.getMonth() === dateB.getMonth();

  const toDateSafe = (value) => {
    if (!value) return null;
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    const source = new AbortController();

    const fetchData = async () => {
      setLoading(true);
      setError("");

      try {
        const headers = {
          authorization: `Bearer ${getToken()}`
        };

        const [masterRes, resourceSummaryRes, titlesRes, copiesRes, loansRes, reservationsRes, finesRes] = await Promise.all([
          axiosInstance.get(`${process.env.REACT_APP_API}/library/getMasterData`, {
            headers,
            signal: source.signal
          }),
          axiosInstance.get(`${process.env.REACT_APP_API}/library/getDashboardResourceSummary`, {
            headers,
            signal: source.signal
          }),
          axiosInstance.get(`${process.env.REACT_APP_API}/library/getTitles`, {
            headers,
            signal: source.signal
          }),
          axiosInstance.get(`${process.env.REACT_APP_API}/library/getCopies`, {
            headers,
            signal: source.signal
          }),
          axiosInstance.get(`${process.env.REACT_APP_API}/library/getLoans`, {
            headers,
            signal: source.signal
          }),
          axiosInstance.get(`${process.env.REACT_APP_API}/library/getReservations`, {
            headers,
            signal: source.signal
          }),
          axiosInstance.get(`${process.env.REACT_APP_API}/library/getFines`, {
            headers,
            signal: source.signal
          })
        ]);

        setMasterData(masterRes.data || { categories: [], locations: [], teachers: [] });
        setResourceSummary(resourceSummaryRes.data || { total_personnel: 0, total_active_students: 0 });
        setTitles(titlesRes.data || []);
        setCopies(copiesRes.data || []);
        setLoans(loansRes.data || []);
        setReservations(reservationsRes.data || []);
        setFines(finesRes.data || []);
      } catch (fetchError) {
        if (fetchError.name === "CanceledError") return;
        setError("ไม่สามารถโหลดข้อมูลห้องสมุดได้ กรุณาลองใหม่อีกครั้ง");
        console.error("LibraryHome fetch error:", fetchError);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
    return () => source.abort();
  }, []);

  const totalAvailable = useMemo(() => {
    return copies.filter((copy) => copy.copy_status === "available").length;
  }, [copies]);

  const dashboardStats = useMemo(() => {
    const now = new Date();

    const loansToday = loans.filter((loan) => {
      const loanDate = toDateSafe(loan.loan_date);
      return loanDate && isSameDay(loanDate, now);
    }).length;

    const loansThisMonth = loans.filter((loan) => {
      const loanDate = toDateSafe(loan.loan_date);
      return loanDate && isSameMonth(loanDate, now);
    }).length;

    const returnsToday = loans.filter((loan) => {
      const returnDate = toDateSafe(loan.returned_date);
      return returnDate && isSameDay(returnDate, now);
    }).length;

    const overdueActive = loans.filter((loan) => {
      if (loan.loan_status === "returned") return false;
      if (loan.loan_status === "overdue") return true;
      return Number(loan.overdue_days || 0) > 0;
    }).length;

    const fineRevenueToday = fines.reduce((sum, fine) => {
      const paidDate = toDateSafe(fine.paid_date);
      if (!paidDate || !isSameDay(paidDate, now)) return sum;
      return sum + Number(fine.paid_amount || 0);
    }, 0);

    const fineRevenueThisMonth = fines.reduce((sum, fine) => {
      const paidDate = toDateSafe(fine.paid_date);
      if (!paidDate || !isSameMonth(paidDate, now)) return sum;
      return sum + Number(fine.paid_amount || 0);
    }, 0);

    const outstandingFine = fines.reduce((sum, fine) => {
      const remain =
        Number(fine.fine_amount || 0) - Number(fine.paid_amount || 0) - Number(fine.waived_amount || 0);
      return sum + Math.max(remain, 0);
    }, 0);

    const queuedReservations = reservations.filter((item) => item.reservation_status === "queued").length;
    const readyReservations = reservations.filter((item) => item.reservation_status === "ready").length;

    return {
      loansToday,
      loansThisMonth,
      returnsToday,
      overdueActive,
      fineRevenueToday,
      fineRevenueThisMonth,
      outstandingFine,
      queuedReservations,
      readyReservations
    };
  }, [loans, reservations, fines]);

  const loanTrendChart = useMemo(() => {
    const now = new Date();
    const rangeDays = timeRange === "7d" ? 7 : timeRange === "30d" ? 30 : 90;
    const labels = [];
    const borrowSeries = [];
    const returnSeries = [];

    for (let i = rangeDays - 1; i >= 0; i -= 1) {
      const day = new Date(now);
      day.setHours(0, 0, 0, 0);
      day.setDate(now.getDate() - i);
      labels.push(
        day.toLocaleDateString("th-TH", {
          day: "2-digit",
          month: "2-digit",
          ...(timeRange === "3m" ? { year: "2-digit" } : {})
        })
      );

      const borrowCount = loans.filter((loan) => {
        const d = toDateSafe(loan.loan_date);
        return d && isSameDay(d, day);
      }).length;

      const returnCount = loans.filter((loan) => {
        const d = toDateSafe(loan.returned_date);
        return d && isSameDay(d, day);
      }).length;

      borrowSeries.push(borrowCount);
      returnSeries.push(returnCount);
    }

    return {
      series: [
        { name: "ยืม", data: borrowSeries },
        { name: "คืน", data: returnSeries }
      ],
      options: {
        chart: { toolbar: { show: false }, parentHeightOffset: 0 },
        stroke: { curve: "smooth", width: 3 },
        dataLabels: { enabled: false },
        colors: ["#0d6efd", "#20c997"],
        xaxis: { categories: labels, labels: { style: { fontSize: "11px" } } },
        yaxis: { min: 0, forceNiceScale: true, labels: { style: { fontSize: "11px" } } },
        legend: { position: "top", horizontalAlign: "right" },
        grid: { borderColor: "rgba(72,94,144,0.12)" }
      }
    };
  }, [loans, timeRange]);

  const fineReasonRevenueChart = useMemo(() => {
    const now = new Date();
    let thresholdDate = null;
    if (timeRange === "7d") {
      thresholdDate = new Date(now);
      thresholdDate.setDate(now.getDate() - 6);
      thresholdDate.setHours(0, 0, 0, 0);
    } else if (timeRange === "30d") {
      thresholdDate = new Date(now);
      thresholdDate.setDate(now.getDate() - 29);
      thresholdDate.setHours(0, 0, 0, 0);
    } else {
      thresholdDate = new Date(now);
      thresholdDate.setMonth(now.getMonth() - 3);
      thresholdDate.setDate(now.getDate() + 1);
      thresholdDate.setHours(0, 0, 0, 0);
    }

    const reasonMap = {
      overdue: 0,
      damage: 0,
      loss: 0,
      other: 0
    };

    fines.forEach((fine) => {
      const paidDate = toDateSafe(fine.paid_date);
      if (!paidDate || paidDate < thresholdDate) return;

      const reasonKey = ["overdue", "damage", "loss", "other"].includes(fine.fine_reason)
        ? fine.fine_reason
        : "other";
      reasonMap[reasonKey] += Number(fine.paid_amount || 0);
    });

    const labels = [
      FINE_REASON_LABELS.overdue,
      FINE_REASON_LABELS.damage,
      FINE_REASON_LABELS.loss,
      FINE_REASON_LABELS.other
    ];
    const values = [reasonMap.overdue, reasonMap.damage, reasonMap.loss, reasonMap.other];

    return {
      series: [{ name: "รายได้ (บาท)", data: values }],
      options: {
        chart: { type: "bar", toolbar: { show: false }, parentHeightOffset: 0 },
        plotOptions: { bar: { borderRadius: 6, columnWidth: "50%" } },
        colors: ["#6f42c1"],
        dataLabels: { enabled: false },
        xaxis: { categories: labels, labels: { style: { fontSize: "11px" } } },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            style: { fontSize: "11px" },
            formatter: (val) => Number(val || 0).toLocaleString("th-TH")
          }
        },
        tooltip: {
          y: {
            formatter: (val) => `${Number(val || 0).toLocaleString("th-TH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} บาท`
          }
        },
        grid: { borderColor: "rgba(72,94,144,0.12)" }
      }
    };
  }, [fines, timeRange]);

  const copyStatusChart = useMemo(() => {
    const labels = ["พร้อมยืม", "กำลังถูกยืม", "ถูกจอง", "ซ่อม", "สูญหาย", "ถอนออก"];
    const values = [
      copies.filter((c) => c.copy_status === "available").length,
      copies.filter((c) => c.copy_status === "on_loan").length,
      copies.filter((c) => c.copy_status === "reserved").length,
      copies.filter((c) => c.copy_status === "repair").length,
      copies.filter((c) => c.copy_status === "lost").length,
      copies.filter((c) => c.copy_status === "withdrawn").length
    ];

    return {
      series: values,
      options: {
        chart: { type: "donut" },
        labels,
        colors: ["#198754", "#ffc107", "#0dcaf0", "#dc3545", "#212529", "#6c757d"],
        legend: { position: "bottom" },
        dataLabels: { enabled: true },
        stroke: { width: 0 }
      }
    };
  }, [copies]);

  const topBorrowedChart = useMemo(() => {
    const countMap = loans.reduce((acc, loan) => {
      const key = String(loan.title_name || "ไม่ระบุชื่อ");
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});

    const ranked = Object.entries(countMap)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);

    return {
      series: [{ name: "จำนวนครั้งที่ยืม", data: ranked.map((item) => item[1]) }],
      options: {
        chart: { type: "bar", toolbar: { show: false }, parentHeightOffset: 0 },
        plotOptions: { bar: { horizontal: true, borderRadius: 4 } },
        colors: ["#fd7e14"],
        dataLabels: { enabled: false },
        xaxis: { categories: ranked.map((item) => item[0]), labels: { style: { fontSize: "11px" } } },
        yaxis: { labels: { style: { fontSize: "11px" } } },
        grid: { borderColor: "rgba(72,94,144,0.12)" }
      }
    };
  }, [loans]);

  const topOverdueLoans = useMemo(() => {
    return [...loans]
      .filter((loan) => loan.loan_status !== "returned" && Number(loan.overdue_days || 0) > 0)
      .sort((a, b) => Number(b.overdue_days || 0) - Number(a.overdue_days || 0))
      .slice(0, 8);
  }, [loans]);

  return (
    <React.Fragment>
      <Header />
      <main className="main main-app p-3 p-lg-4">
        <div className="d-flex align-items-center justify-content-between mb-3">
          <h4 className="mb-0">แดชบอร์ดห้องสมุด</h4>
          <Badge bg="primary">เชื่อมต่อข้อมูลแล้ว</Badge>
        </div>

        {/* Quick Navigation */}
        <Row className="g-2 mb-4">
          {quickNavItems.map(item => (
            <Col key={item.link} xs={6} sm={4} md={3} xl={2}>
              <Card
                className="h-100 text-center border-0 shadow-sm"
                style={{ cursor: "pointer" }}
                onClick={() => navigate(item.link)}
              >
                <Card.Body className="py-3 px-2">
                  <i className={`${item.icon} fs-3 text-primary mb-2 d-block`} />
                  <small className="fw-medium">{item.label}</small>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>

        {error && (
          <Alert variant="danger" className="mb-3">
            {error}
          </Alert>
        )}

        <Row className="g-3 mb-3">
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <small className="text-muted">การยืมวันนี้</small>
                <h3 className="mb-1">{formatNumber(dashboardStats.loansToday)}</h3>
                <div className="small text-muted">คืนวันนี้ {formatNumber(dashboardStats.returnsToday)} รายการ</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <small className="text-muted">การยืมเดือนนี้</small>
                <h3 className="mb-1">{formatNumber(dashboardStats.loansThisMonth)}</h3>
                <div className="small text-muted">รายการเกินกำหนด {formatNumber(dashboardStats.overdueActive)} รายการ</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <small className="text-muted">รายได้ค่าปรับเดือนนี้ (บาท)</small>
                <h3 className="mb-1">{formatCurrency(dashboardStats.fineRevenueThisMonth)}</h3>
                <div className="small text-muted">รายได้วันนี้ {formatCurrency(dashboardStats.fineRevenueToday)}</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="h-100">
              <Card.Body>
                <small className="text-muted">ค่าปรับค้างรับ (บาท)</small>
                <h3 className="mb-1">{formatCurrency(dashboardStats.outstandingFine)}</h3>
                <div className="small text-muted">
                  คิวจองรอ {formatNumber(dashboardStats.queuedReservations)} | พร้อมรับ {formatNumber(dashboardStats.readyReservations)}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col xl={7}>
            <Card className="h-100">
              <Card.Header className="fw-semibold d-flex justify-content-between align-items-center gap-2 flex-wrap">
                <span>แนวโน้มยืม-คืนย้อนหลัง</span>
                <ButtonGroup size="sm">
                  {TIME_RANGE_OPTIONS.map((option) => (
                    <ToggleButton
                      key={option.value}
                      id={`library-time-range-${option.value}`}
                      type="radio"
                      variant={timeRange === option.value ? "primary" : "outline-primary"}
                      name="library-time-range"
                      value={option.value}
                      checked={timeRange === option.value}
                      onChange={(event) => setTimeRange(event.currentTarget.value)}
                    >
                      {option.label}
                    </ToggleButton>
                  ))}
                </ButtonGroup>
              </Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5"><Spinner animation="border" size="sm" /> กำลังโหลด...</div>
                ) : (
                  <ReactApexChart options={loanTrendChart.options} series={loanTrendChart.series} type="line" height={300} />
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col xl={5}>
            <Card className="h-100">
              <Card.Header className="fw-semibold">โครงสร้างสถานะเล่มหนังสือ</Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5"><Spinner animation="border" size="sm" /> กำลังโหลด...</div>
                ) : (
                  <ReactApexChart options={copyStatusChart.options} series={copyStatusChart.series} type="donut" height={300} />
                )}
                <div className="small text-muted mt-2">
                  เล่มทั้งหมด {formatNumber(copies.length)} | พร้อมยืม {formatNumber(totalAvailable)}
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mb-3">
          <Col lg={12}>
            <Card>
              <Card.Header className="fw-semibold">รายได้ค่าปรับแยกตามเหตุผล ({TIME_RANGE_OPTIONS.find((item) => item.value === timeRange)?.label})</Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5"><Spinner animation="border" size="sm" /> กำลังโหลด...</div>
                ) : (
                  <ReactApexChart
                    options={fineReasonRevenueChart.options}
                    series={fineReasonRevenueChart.series}
                    type="bar"
                    height={300}
                  />
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3">
          <Col lg={7}>
            <Card>
              <Card.Header className="fw-semibold">Top หนังสือที่ถูกยืมบ่อย</Card.Header>
              <Card.Body>
                {loading ? (
                  <div className="text-center py-5"><Spinner animation="border" size="sm" /> กำลังโหลด...</div>
                ) : (
                  <ReactApexChart options={topBorrowedChart.options} series={topBorrowedChart.series} type="bar" height={290} />
                )}
              </Card.Body>
            </Card>
          </Col>

          <Col lg={5}>
            <Card>
              <Card.Header className="fw-semibold">รายการเกินกำหนดสูงสุด</Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th>ผู้ยืม</th>
                      <th>หนังสือ</th>
                      <th className="text-end">เกินกำหนด (วัน)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && topOverdueLoans.length === 0 && (
                      <tr>
                        <td colSpan={3} className="text-center text-muted py-3">
                          ไม่พบรายการเกินกำหนด
                        </td>
                      </tr>
                    )}
                    {topOverdueLoans.map((loan) => (
                      <tr key={loan.loan_id}>
                        <td>
                          {loan.borrower_name}
                          <div className="small text-muted">{loan.borrower_idnumber}</div>
                        </td>
                        <td>{loan.title_name}</td>
                        <td className="text-end text-danger fw-semibold">{formatNumber(loan.overdue_days)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Row className="g-3 mt-0">
          <Col lg={6}>
            <Card>
              <Card.Header className="fw-semibold">ภาพรวมทรัพยากรห้องสมุด</Card.Header>
              <Card.Body>
                <div className="small mb-2">รายการหนังสือ: {formatNumber(titles.length)} ชื่อเรื่อง</div>
                <div className="small mb-2">เล่มทั้งหมด: {formatNumber(copies.length)} เล่ม</div>
                <div className="small mb-2">เล่มพร้อมให้ยืม: {formatNumber(totalAvailable)} เล่ม</div>
                <div className="small mb-2">หมวดหมู่: {formatNumber(masterData.categories.length)} หมวด</div>
                <div className="small mb-2">ตำแหน่งจัดเก็บ: {formatNumber(masterData.locations.length)} ตำแหน่ง</div>
                <div className="small mb-2">บุคลากรทั้งหมด: {formatNumber(resourceSummary.total_personnel)} คน</div>
                <div className="small mb-0">นักเรียนทั้งหมด: {formatNumber(resourceSummary.total_active_students)} คน</div>
              </Card.Body>
            </Card>
          </Col>

          <Col lg={6}>
            <Card>
              <Card.Header className="fw-semibold">สถานะการจองล่าสุด</Card.Header>
              <Card.Body className="p-0">
                <Table responsive hover className="mb-0">
                  <thead className="table-secondary">
                    <tr>
                      <th>Resv No</th>
                      <th>ผู้จอง</th>
                      <th>หนังสือ</th>
                      <th>สถานะ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {!loading && reservations.length === 0 && (
                      <tr>
                        <td colSpan={4} className="text-center text-muted py-3">
                          ไม่พบข้อมูลการจอง
                        </td>
                      </tr>
                    )}
                    {reservations.slice(0, 8).map((row) => (
                      <tr key={row.resv_id}>
                        <td>{row.resv_no}</td>
                        <td>
                          {row.borrower_name}
                          <div className="small text-muted">{row.borrower_idnumber}</div>
                        </td>
                        <td>{row.title_name}</td>
                        <td>
                          <Badge
                            bg={
                              row.reservation_status === "ready"
                                ? "info"
                                : row.reservation_status === "queued"
                                ? "warning"
                                : row.reservation_status === "fulfilled"
                                ? "success"
                                : row.reservation_status === "cancelled"
                                ? "danger"
                                : "secondary"
                            }
                          >
                            {RESERVATION_STATUS_LABELS[row.reservation_status] || row.reservation_status || "-"}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </main>
      <Footer />
    </React.Fragment>
  );
}
